import type { PaginatorState } from 'primeng/paginator';
import { HttpParams } from '@angular/common/http';
import type { TableLazyLoadEvent, TablePageEvent } from 'primeng/table';
import { Cloneable } from '@bindoc-projects/util-domain-lib';

/**
 * Interface for api responses, that use some pageable functions from Spring Boot
 */
export interface BdPageable<T> {
  content: T[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: {
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    sort: BdPageableSort;
    unpaged: boolean;
  };
  size: number;
  sort: BdPageableSort;
  totalElements: number;
  totalPages: number;
}

export interface BdPageableSort {
  sorted: false;
  unsorted: true;
  empty: true;
}

export class BdPageableHelper {
  public static empty<T = unknown>(): BdPageable<T> {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      sort: null,
      size: 0,
      number: 0,
      last: true,
      first: true,
      empty: true,
      numberOfElements: 0,
      pageable: null,
    };
  }

  /**
   * Transform pagination response to types pagination response with given mapping function.
   * @param response - pagination response (containing content, numberOfElements, ...)
   * @param itemFn
   */
  public static responseToPage<T, TSource>(
    response: BdPageable<TSource>,
    itemFn: (item: TSource) => T,
  ): BdPageable<T> {
    return {
      ...response,
      content: response.content.map((item) => itemFn(item)),
    };
  }
}

export class Paginator implements PaginatorState, Cloneable<Paginator> {
  public static toHttpParams(paginator: PaginatorState): HttpParams {
    let params = new HttpParams();
    params = params.set('size', paginator.rows.toString());
    params = params.set('page', paginator.page.toString());
    return params;
  }

  public static from(event: TableLazyLoadEvent | TablePageEvent): Paginator {
    return new Paginator(event.rows, Math.ceil(event.first / event.rows));
  }

  public static of(create: {
    pageSize?: number;
    pageIndex?: number;
    query?: string;
    sort?: string[];
  }): Paginator {
    const p = new Paginator(create?.pageSize ?? 10, create?.pageIndex ?? 0);
    p.query = create?.query ?? undefined;
    p.sort = create?.sort ?? undefined;
    return p;
  }

  /**
   * Build a Spring-Data sort expression (`field,direction`) from a PrimeNG
   * sort order (`1` = ascending, `-1` = descending).
   */
  public static sortExpression(field: string, order: 1 | -1): string {
    return `${field},${order === 1 ? 'asc' : 'desc'}`;
  }

  public totalRecords: number;
  public first: number;
  public pageCount: number;
  public rows: number;
  public page: number;
  public query: string | undefined;
  public sort: string[] | undefined;

  private constructor(
    public pageSize: number,
    public pageIndex: number,
  ) {
    this.totalRecords = 0;
    this.page = pageIndex;
    this.rows = this.pageSize;
    this.first = this.pageSize * this.pageIndex;
  }

  public updateQuery(query: string): void {
    this.query = query;
    this.pageIndex = 0;
  }

  public updatePage(event: TableLazyLoadEvent | TablePageEvent): void {
    this.pageSize = event.rows;
    this.pageIndex = Math.ceil(event.first / event.rows);
  }

  /**
   * Apply a PrimeNG single-column sort event. Builds the Spring-Data sort
   * expression and resets to the first page (consistent with `updateQuery`).
   * A missing/multi-column field or a neutral order clears the sort.
   * Returns false for a repeated event, preserving the page and avoiding reload
   * loops when PrimeNG reapplies sorting after table data changes.
   */
  public updateSort(field?: string | string[], order?: number): boolean {
    const sort =
      typeof field === 'string' && (order === 1 || order === -1)
        ? [Paginator.sortExpression(field, order)]
        : undefined;
    if (
      (this.sort?.length ?? 0) === (sort?.length ?? 0) &&
      this.sort?.[0] === sort?.[0]
    ) {
      return false;
    }
    this.sort = sort;
    this.pageIndex = 0;
    return true;
  }

  /**
   * Append this paginator's complete query representation — `query`, `page`,
   * `size` and the repeated `sort` expressions — to the given `HttpParams`.
   * This is the single place that defines how a `Paginator` reaches a
   * Spring-Data backed API; services must not rebuild it by hand.
   *
   * An empty `query` is omitted, so a cleared search box does not send
   * `query=`.
   */
  public appendTo(params: HttpParams): HttpParams {
    let next = params;

    if (this.query) {
      next = next.set('query', this.query);
    }

    next = next.set('page', this.pageIndex).set('size', this.pageSize);

    return this.appendSort(next);
  }

  /**
   * Append this paginator's sort expressions to the given `HttpParams` as
   * repeated `sort` query parameters (Spring-Data convention). Returns the
   * params unchanged when no sort is set.
   */
  public appendSort(params: HttpParams): HttpParams {
    if (!this.sort) {
      return params;
    }
    let next = params;
    for (const expression of this.sort) {
      next = next.append('sort', expression);
    }
    return next;
  }

  clone(): Paginator {
    const p = new Paginator(this.pageSize, this.pageIndex);
    p.totalRecords = this.totalRecords;
    p.first = this.first;
    p.pageCount = this.pageCount;
    p.rows = this.rows;
    p.page = this.page;
    p.query = this.query;
    p.sort = this.sort ? [...this.sort] : undefined;
    return p;
  }
}
